import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { GameVariant } from '@draughts/shared';
import AnimatedShowcaseBoard from './AnimatedShowcaseBoard';
import FeaturesGrid from './FeaturesGrid';
import HowItWorks from './HowItWorks';
import LandingPuzzle from './LandingPuzzle';
import VariantsShowcase from './VariantsShowcase';
import CtaBanner from './CtaBanner';

/**
 * Il corpo della landing: eroe, sezioni di presentazione, vetrina delle varianti.
 *
 * E' stato estratto da HomePage per una ragione sola: questo blocco viene reso
 * DUE volte, una dall'app e una dallo script di prerender, che non puo' montare
 * HomePage perche' quella legge gli store di autenticazione e multigiocatore.
 * Tenerne due copie avrebbe voluto dire due testi che divergono — e il testo di
 * questa pagina e' esattamente cio' che un motore di ricerca indicizza.
 *
 * Puramente presentazionale: nessuno store, nessun effetto, solo callback. Nel
 * prerender arrivano dei no-op e l'HTML e' comunque completo.
 */
export interface LandingSectionsProps {
  /**
   * L'unico CTA della hero: gioca subito (accesso ospite se serve).
   *
   * La sfida al computer e i tornei stavano qui accanto e sono andati via: sono
   * due delle sei porte del foglio di «Gioca» (FoglioGioca.tsx), che ora si
   * apre dalla voce di barra a ogni larghezza — anche per un visitatore, e
   * anche su questa pagina. Una porta, un nome, e un invito in cima.
   */
  onPlayNow: () => void;
  onSolveDemoPuzzle: () => void;
  onOpenVariantRules?: (variant: GameVariant) => void;
  /** All'elenco completo delle varianti, dal fondo della vetrina. */
  onOpenGameRules?: () => void;
  /** Accesso ospite in corso: il bottone principale mostra l'attesa. */
  playPending?: boolean;
  /** Chiave i18n dell'errore di accesso ospite, o null. */
  guestErrorKey?: string | null;
}

export default function LandingSections({
  onPlayNow,
  onSolveDemoPuzzle,
  onOpenVariantRules,
  onOpenGameRules,
  playPending = false,
  guestErrorKey = null,
}: LandingSectionsProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Hero section */}
      <div className="home-hero">
        <div className="home-hero-content">
          {/* Nome del sito — letterale, uguale in tutte le lingue.
              Vedi packages/web/src/__tests__/i18n-brand-terms.test.ts.
              I due span NON sono due pezzi traducibili: sono due caselle,
              e la sfumatura ne vuole due per ripartire sul «G». Vedi
              `.brand-part` in HomePage.css. */}
          <span className="home-hero-brand"><span className="brand-part">Draughts</span><span className="brand-part">Game.com</span></span>
          <h1 className="home-hero-title">
            {t('home.landing.heroTitle')} <span className="home-hero-accent">{t('home.landing.heroTitleAccent')}</span>
          </h1>
          <p className="home-hero-tagline">{t('home.landing.heroTagline')}</p>

          {/* Un modo di giocare, non un elenco. Accedi e Registrati stavano qui
              e sono andati via — l'accesso vive nella navbar, che c'è sempre, e
              da dentro quel modale si arriva alla registrazione. Poi se ne sono
              andati anche «Sfida AI» e «Tornei»: erano due righe del foglio di
              «Gioca» ridette in cima, e il foglio ora c'è a ogni larghezza. */}
          <div className="home-cta-group">
            <button
              className="home-cta-primary"
              onClick={onPlayNow}
              disabled={playPending}
            >
              {playPending ? (
                <>
                  <span className="home-cta-icon"><Loader2 size={16} className="home-cta-spinner" /></span>
                  {t('online.connecting')}
                </>
              ) : (
                /* Chiave propria, non `dashboard.hero.ctaPlayNow`: la landing
                   puo' ritoccare il suo invito senza toccare la dashboard e il
                   foglio di «Gioca», che condividono `rules.playOnline`. Oggi
                   dicono tutte «Gioca subito» — la barra, che dice «Gioca» e
                   basta, resta l'unica a chiamarsi come l'indice. */
                t('home.landing.heroCta')
              )}
            </button>

            {/* Il fallimento dell'ingresso da ospite, DETTO. Sotto il gruppo
                di CTA e non dentro il bottone: il bottone deve tornare
                premibile, e il motivo deve restare leggibile mentre lo si
                ripreme. `role="alert"` perché compare dopo un gesto. */}
            {guestErrorKey && (
              <p className="home-cta-error" role="alert">{t(guestErrorKey)}</p>
            )}
          </div>
        </div>

        <div className="home-hero-board">
          <AnimatedShowcaseBoard />
        </div>
      </div>

      {/* Landing sections */}
      <FeaturesGrid />
      <HowItWorks />
      <LandingPuzzle onSolve={onSolveDemoPuzzle} />
      <VariantsShowcase onSelectVariant={onOpenVariantRules} onOpenGameRules={onOpenGameRules} />
      <CtaBanner onAction={onPlayNow} />
    </>
  );
}
